import React, { Suspense, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components/macro';
import { Helmet } from 'react-helmet';
import { isArray } from 'lodash';

import { useTopicFilters } from 'components/TopicFilters/utils/useTopicFilters';
import pluralize from 'helpers/pluralize';
import getFiltersFromUrl from 'helpers/getFiltersFromUrl';
import getLocationNames from './helpers/getLocationNames';

import useMedia from 'hooks/useMedia';
import Content from 'components/styled/Content/Content';
import ScrollTopOnMount from 'components/utility/ScrollTopOnMount/ScrollTopOnMount';
import NotificationSettingsModal from '../../components/notifications/Settings';
import BackToTop from 'components/BackToTop/BackToTop';
import Footer from 'components/Footer/Footer';
import TopListsHeader from 'components/TopLists/TopListHeader/TopListsHeader';
import TopicFilters from 'components/TopicFilters/TopicFilters';
import PerformanceTrendSkeletonLoader from 'components/data-containers/PerformanceTrend/SkeletonLoader';
import PlaylistTable from 'components/data-containers/PlaylistTable';
import { initialSocials } from 'components/TopPerformingArtistsOnSocial';

import PositionsTableSkeleton from 'components/data-containers/PositionsTable/SkeletonLoader';
import salesTypes from 'maps/sales-types';
import { ITopLists, TopListsContext } from 'context/topLists';
import { RootState } from 'redux/rootReducer';
import BrowseEntitiesSkeleton from 'components/data-containers/BrowseEntities/SkeletonLoader';
import { SalesTypes } from 'context/entityContext';
import SkeletonLoader from 'components/TopPerformingArtistsOnSocial/SkeletonLoader';

// import TopCertificationSkeletonLoader from 'components/Certification/TopCertifications/TopCertificationSkeletonLoader';

const StreamingPerformanceTrend = React.lazy(
  () =>
    import(
      'components/data-containers/PerformanceTrend/PerformanceComponent/PerformanceComponent'
    )
);

const SourceOfStreams = React.lazy(
  () => import('components/data-containers/PerformanceTrend/SourceOfStreams')
);

const BrowseTopListsEntity = React.lazy(
  (): any =>
    import('components/data-containers/BrowseEntities/BrowseTopListsEntity')
);

const TopChartPositionsTable = React.lazy(
  () =>
    import(
      'components/data-containers/PositionsTable/TopChartPositionsTable'
    ) as any
);

const TopPerformingArtistsOnSocial = React.lazy(
  (): any => import('components/TopPerformingArtistsOnSocial')
);
// const TopCertifications = React.lazy((): any =>
//   import('components/Certification/TopCertifications/TopCertifications')
// );

const Main = styled.div.attrs({
  role: 'main',
})`
  position: relative;
  z-index: 1;
  flex: auto;
  display: flex;
  flex-flow: column nowrap;
  background-color: ${({ theme }): string => theme.LightGrey};
`;

const TopLists: React.FC<{ location: any }> = ({ location }) => {
  const isLargeScreen = useMedia('(min-width: 60em)');
  const [filters] = useTopicFilters();
  const allCountries = useSelector((state: RootState) => state.countries.data);
  const allExCountries = useSelector(
    (state: RootState) => state.exCountries.data
  );
  const allRegions = useSelector((state: RootState) => state.regions.data);
  const filtersTitles = getLocationNames({
    allCountries,
    allExCountries,
    allRegions,
    filters,
  });
  const subtitle = `, ${filtersTitles ? filtersTitles : 'Worldwide'}`;
  const streamingPerformanceTitle = `Streaming Performance${subtitle}`;

  const customRegions = useSelector(
    (state: RootState) => state.customRegions.data
  );
  const customRegionFilter =
    getFiltersFromUrl(location)?.customRegion && !isArray(customRegions)
      ? getFiltersFromUrl(location)?.customRegion
      : undefined;
  const context: ITopLists = {
    type: 'worldwide',
    topic: [] as any,
    filters,
    customRegionFilter,
  };
  const contextValue = Object.assign({}, context, {
    topic: ['streaming', 'performance'],
  });
  const getEntityTitle = (entity: string, countryName: string): string =>
    `Top ${pluralize(entity)}${countryName}`;
  const browseTracksTitle = useMemo(
    () => getEntityTitle('track', subtitle),
    [subtitle]
  );
  const browseAlbumTitle = useMemo(
    () => getEntityTitle('album', subtitle),
    [subtitle]
  );
  const [selectedSalesType, setSelectedSalesType] = useState<SalesTypes>();
  const topPerformingArtistsOnSocialTitle =
    'Top Performing Artists on Social, Worldwide';

  return (
    <>
      <ScrollTopOnMount />
      <Helmet defer={false} title="Top Lists" />

      <NotificationSettingsModal />

      <TopListsHeader />

      <Main>
        <Content>
          {/* Note: Do not remove the next <div>. It avoids flexbox to double margins between generic components. */}
          <div>
            <TopicFilters
              topic={'topLists'}
              selectedSalesType={selectedSalesType}
            />
            <TopListsContext.Provider value={contextValue}>
              <Suspense
                fallback={
                  <PerformanceTrendSkeletonLoader
                    title={streamingPerformanceTitle}
                    dropdownLabel="All DSPs"
                    toggleOptions={[
                      { value: 'aggregate', label: 'All DSPs', active: true },
                      { value: 'all', label: 'By DSP', active: false },
                    ]}
                    showPeriodDropdown
                    isWide
                    isNarrowScreen
                  />
                }
              >
                <StreamingPerformanceTrend
                  componentId="toplists-streaming-performance-trend"
                  salesType={salesTypes.streaming}
                  title={streamingPerformanceTitle}
                  accessBreakdown={false}
                  isTopLists
                  withCustomRange
                />
              </Suspense>

              <Suspense
                fallback={
                  <PerformanceTrendSkeletonLoader
                    title={`Source of Streams${subtitle}`}
                    showPeriodDropdown
                    isWide
                  />
                }
              >
                <SourceOfStreams
                  salesType={salesTypes.streaming}
                  subtitle={subtitle}
                  filters={filters}
                  isTopList
                />
              </Suspense>
            </TopListsContext.Provider>

            <Suspense
              fallback={
                <BrowseEntitiesSkeleton
                  title={browseTracksTitle}
                  browseEntityType="track"
                  showToggle
                  enableSearch={false}
                  isBrowseTable
                  showTabs={false}
                  isFakeSortToggle
                  isNarrowScreen
                  browseRelated
                />
              }
            >
              <BrowseTopListsEntity
                genericTitle={browseTracksTitle}
                globalFilters={filters}
                initialEntity="track"
                setSelectedSalesType={setSelectedSalesType}
                customRegionValue={customRegionFilter}
              />
            </Suspense>

            <Suspense
              fallback={
                <BrowseEntitiesSkeleton
                  title={browseAlbumTitle}
                  browseEntityType="release"
                  showToggle
                  enableSearch={false}
                  isBrowseTable
                  showTabs={false}
                  isFakeSortToggle
                  isNarrowScreen
                  browseRelated
                />
              }
            >
              <BrowseTopListsEntity
                genericTitle={browseAlbumTitle}
                globalFilters={filters}
                initialEntity="release"
                customRegionValue={customRegionFilter}
              />
            </Suspense>

            <Suspense
              fallback={
                <PositionsTableSkeleton
                  title={`Top Chart Positions${subtitle}`}
                  productType="track"
                  variation="currentPosition"
                />
              }
            >
              <TopChartPositionsTable
                topicFilters={filters}
                title={`Top Chart Positions${subtitle}`}
                isLargeScreen={isLargeScreen}
              />
            </Suspense>

            <PlaylistTable
              outerFilters={filters}
              subtitle={subtitle}
              isTopList
              customRegion={customRegionFilter}
            />

            <Suspense
              fallback={
                <SkeletonLoader
                  initialTab={{ label: 'Spotify', value: 'Spotify' }}
                  isLargeScreen={isLargeScreen}
                  title={topPerformingArtistsOnSocialTitle}
                  toggleOptions={initialSocials}
                />
              }
            >
              <TopPerformingArtistsOnSocial
                title={topPerformingArtistsOnSocialTitle}
                isLargeScreen={isLargeScreen}
              />
            </Suspense>

            {/* <Suspense
            fallback={
              <TopCertificationSkeletonLoader
                title={`Latest Certifications${subtitle}`}
              />
            }
          >
            <TopCertifications
              topicFilters={filters}
              title={`Latest Certifications${subtitle}`}
              isLargeScreen={isLargeScreen}
            />
          </Suspense> */}
          </div>
        </Content>
      </Main>
      <BackToTop />
      <Footer />
    </>
  );
};

export default TopLists;
